export interface ResidenceCountryOption {
  value: string
  label: string
}

/** cr-core `/api/v1/countries` yanıtındaki ülke kaydı. */
export interface CarRentalCountryApiItem {
  id?: number
  code?: string
  name?: string
  slug?: string
  trip?: string
  timezone?: string
}

/** Araç kiralama ikamet ülkesi seçicisinin kapsamı; car search sayfasındaki liste ile aynı ISO kodları. */
export const RESIDENCE_COUNTRY_CODES = [
  'ae',
  'am',
  'ar',
  'at',
  'au',
  'az',
  'be',
  'bg',
  'bh',
  'br',
  'ca',
  'ch',
  'cl',
  'cn',
  'co',
  'cy',
  'cz',
  'de',
  'dk',
  'dz',
  'ee',
  'eg',
  'es',
  'fi',
  'fr',
  'gb',
  'ge',
  'gr',
  'hr',
  'hu',
  'id',
  'ie',
  'il',
  'in',
  'iq',
  'is',
  'it',
  'jo',
  'jp',
  'ke',
  'kg',
  'kr',
  'kw',
  'kz',
  'lb',
  'lt',
  'lu',
  'lv',
  'ma',
  'md',
  'mk',
  'mt',
  'mx',
  'my',
  'ng',
  'nl',
  'no',
  'nz',
  'om',
  'pe',
  'ph',
  'pk',
  'pl',
  'pt',
  'qa',
  'ro',
  'rs',
  'ru',
  'sa',
  'se',
  'sg',
  'si',
  'sk',
  'th',
  'tj',
  'tm',
  'tr',
  'tw',
  'ua',
  'us',
  'uz',
  'vn',
  'za',
]

const FALLBACK_RESIDENCE_COUNTRY = 'us'

const normalizeLanguage = (language?: string | null) => {
  const segments = (language || 'en').trim().toLowerCase().split('-').filter(Boolean)

  return segments.at(-1) || 'en'
}

/** Ülke adları Intl.DisplayNames ile çevrilir; ICU'da dil yoksa ISO kodu gösterilir. */
export const buildResidenceCountryOptions = (language?: string | null): ResidenceCountryOption[] => {
  const locale = normalizeLanguage(language)

  let displayNames: Intl.DisplayNames | undefined
  try {
    displayNames = new Intl.DisplayNames([locale], { type: 'region' })
  } catch (error) {
    console.error('buildResidenceCountryOptions: Intl.DisplayNames failed', error)
    displayNames = undefined
  }

  return RESIDENCE_COUNTRY_CODES.map((code) => {
    const upperCode = code.toUpperCase()

    return {
      value: code,
      label: displayNames?.of(upperCode) || upperCode,
    }
  }).sort((a, b) => a.label.localeCompare(b.label, locale))
}

/**
 * Aynı ISO kodunda birden fazla kayıt gelirse (ör. GB: Great Britain / Northern Ireland)
 * ana ülke kaydını tercih eder.
 */
const preferCarRentalCountry = (existing: CarRentalCountryApiItem, incoming: CarRentalCountryApiItem) => {
  if (incoming.slug === 'buyuk-britanya') return true
  if (existing.slug === 'kuzey-irlanda' && incoming.slug !== 'kuzey-irlanda') return true
  if (incoming.timezone === 'Europe/London' && existing.timezone !== 'Europe/London') return true

  return false
}

/** cr-core countries API yanıtını CarRentalSearchForm `residenceCountries` formatına çevirir. */
export const mapCarRentalCountriesToResidenceOptions = (countries: CarRentalCountryApiItem[], language?: string | null): ResidenceCountryOption[] => {
  const locale = normalizeLanguage(language)
  const byCode = new Map<string, CarRentalCountryApiItem>()

  for (const country of countries) {
    const code = country?.code?.trim()
    const name = country?.name?.trim()
    if (!code || !name) continue

    const key = code.toLowerCase()
    const existing = byCode.get(key)

    if (!existing || preferCarRentalCountry(existing, country)) {
      byCode.set(key, country)
    }
  }

  return Array.from(byCode.entries())
    .map(([value, country]) => ({
      value,
      label: country.name?.trim() || value.toUpperCase(),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, locale))
}

export const resolveResidenceCountry = (market?: string | null, countries?: ResidenceCountryOption[]): string => {
  const normalized = (market || '').split('-')[0].toLowerCase()
  const list = countries?.length ? countries : buildResidenceCountryOptions()

  return list.some((country) => country.value === normalized) ? normalized : FALLBACK_RESIDENCE_COUNTRY
}
